import { useState, useEffect, useCallback } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import { t } from '../i18n';

const API_BASE = '/api/matrimony';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  const token = getAuthToken();
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Token ${token}` } : {}) };
}

const colors = {
  bg: '#F7F5F0', surface: '#fff', border: '#E3E1D9', text: '#1F1E1B',
  textMuted: '#5F5E5A', primary: '#0F6E56', primaryBg: '#E1F5EE', danger: '#A32D2D', gold: '#F2C879',
};

const MARITAL_STATUS = [['never_married', 'Never Married'], ['divorced', 'Divorced'], ['widowed', 'Widowed'], ['annulled', 'Annulled']];
const DIET_OPTIONS = [['veg', 'Vegetarian'], ['non_veg', 'Non-Vegetarian'], ['eggetarian', 'Eggetarian'], ['vegan', 'Vegan']];

function MatrimonyHelpModal({ onClose }) {
  const sectionStyle = { marginBottom: 18 };
  const headingStyle = { fontSize: 15, fontWeight: 600, margin: '0 0 6px' };
  const textStyle = { fontSize: 13, color: '#3A3935', lineHeight: 1.5, margin: '0 0 6px' };
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 600, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 19 }}>{t('mth_title')}</h2>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('mtr_setup')}</p>
          <p style={textStyle}>{t('mth_p1')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('mth_p2_q')}</p>
          <p style={textStyle}>{t('mth_p2')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('mth_p3_q')}</p>
          <p style={textStyle}>{t('mth_p3')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('mth_p4_q')}</p>
          <p style={textStyle}>{t('mth_p4')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('mth_p5_q')}</p>
          <p style={textStyle}>{t('mth_p5')}</p>
        </div>
        <div style={sectionStyle}>
          <p style={headingStyle}>{t('mth_p6_q')}</p>
          <p style={textStyle}>{t('mth_p6')}</p>
        </div>
        <button onClick={onClose} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('got_it')}</button>
      </div>
    </div>
  );
}

function GalleryManager({ profile, onChanged }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/profile/gallery/upload/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not upload.');
      onChanged();
    } catch (err) { alert(err.message); } finally { setUploading(false); }
  };

  return (
    <div>
      <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>Gallery photos ({profile.gallery?.length || 0}/5)</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {(profile.gallery || []).map((url, i) => (
          <img key={i} src={url} alt="" style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 8, border: `0.5px solid ${colors.border}` }} />
        ))}
      </div>
      {(profile.gallery?.length || 0) < 5 && (
        <label style={{ display: 'inline-block', padding: '6px 14px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 16, background: '#fff', cursor: 'pointer' }}>
          {uploading ? 'Uploading...' : '+ Add photo'}
          <input type="file" accept="image/*" onChange={upload} style={{ display: 'none' }} disabled={uploading} />
        </label>
      )}
    </div>
  );
}

function ProfileSetupForm({ existing, onSaved }) {
  const [form, setForm] = useState(existing || {
    gender: 'male', date_of_birth: '', height_cm: '', marital_status: 'never_married',
    city: '', country: '', education: '', profession: '', income_range: '',
    religion: '', sect: '', mother_tongue: '', diet: '',
    father_occupation: '', siblings_count: '', family_details: '',
    bio: '', looking_for: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasBlueTick, setHasBlueTick] = useState(false);
  const [editing, setEditing] = useState(false);
  const [autoPost, setAutoPost] = useState(false);

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 14, border: '0.5px solid #B4B2A9', borderRadius: 6, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 13, color: colors.textMuted, display: 'block', marginBottom: 4 };
  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    fetch('/api/blue-tick/', { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setHasBlueTick(!!d.has_blue_tick))
      .catch(() => {});
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/profile/save/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ...form, auto_post: autoPost }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not save profile.');

      if (photoFile) {
        const fd = new FormData();
        fd.append('photo', photoFile);
        const token = getAuthToken();
        await fetch(`${API_BASE}/profile/upload-photo/`, { method: 'POST', headers: token ? { Authorization: `Token ${token}` } : {}, body: fd });
      }
      alert('Profile saved!');
      setEditing(false);
      onSaved();
    } catch (e) { alert(networkMessage(e)); } finally { setSubmitting(false); }
  };

  if (existing && !editing) {
    return (
      <div style={{ background: '#fff', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {existing.photo && <img src={existing.photo} alt="profile" style={{ width: 110, height: 110, objectFit: 'cover', borderRadius: 12, border: `0.5px solid ${colors.border}` }} />}
          <div style={{ flex: 1, minWidth: 200 }}>
            <p style={{ fontSize: 16, fontWeight: 700, margin: '0 0 4px' }}>{existing.city || 'Your profile'}{existing.country ? `, ${existing.country}` : ''}</p>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 6px' }}>{[existing.gender, existing.marital_status, existing.education, existing.profession].filter(Boolean).join(' · ')}</p>
            {existing.religion && <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 6px' }}>{[existing.religion, existing.sect, existing.mother_tongue].filter(Boolean).join(' · ')}</p>}
            {existing.bio && <p style={{ fontSize: 13, margin: '0 0 6px', whiteSpace: 'pre-wrap' }}>{existing.bio}</p>}
            {existing.looking_for && <p style={{ fontSize: 12, color: colors.textMuted, margin: 0 }}><b>{t('mtr_looking')}</b> {existing.looking_for}</p>}
          </div>
        </div>
        <button onClick={() => setEditing(true)} style={{ marginTop: 12, padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>✏️ {t('mtr_edit')}</button>
        <div style={{ marginTop: 16, borderTop: `0.5px solid ${colors.border}`, paddingTop: 12 }}><GalleryManager profile={existing} onChanged={onSaved} /></div>
      </div>
    );
  }

  return (
    <div style={{ background: '#F1EFE8', border: `0.5px solid ${colors.border}`, borderRadius: 12, padding: '1.25rem' }}>
      <p style={{ fontWeight: 500, fontSize: 15, margin: '0 0 12px' }}>{existing ? t('mtr_edit_your') : t('mtr_setup')}</p>
      <div style={{ display: 'grid', gap: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('gender')}</label>
            <select style={inputStyle} value={form.gender} onChange={update('gender')}>
              <option value="male">{t('male')}</option><option value="female">{t('female')}</option>
            </select>
          </div>
          <div><label style={labelStyle}>{t('date_of_birth')}</label><input type="date" style={inputStyle} value={form.date_of_birth || ''} onChange={update('date_of_birth')} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('height_cm')}</label><input type="number" style={inputStyle} value={form.height_cm || ''} onChange={update('height_cm')} placeholder="e.g. 175" /></div>
          <div><label style={labelStyle}>{t('marital_status')}</label>
            <select style={inputStyle} value={form.marital_status} onChange={update('marital_status')}>
              {MARITAL_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('city')}</label><input style={inputStyle} value={form.city} onChange={update('city')} /></div>
          <div><label style={labelStyle}>{t('country')}</label><input style={inputStyle} value={form.country} onChange={update('country')} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('education_label')}</label><input style={inputStyle} value={form.education} onChange={update('education')} /></div>
          <div><label style={labelStyle}>{t('profession_label')}</label><input style={inputStyle} value={form.profession} onChange={update('profession')} /></div>
        </div>
        <div><label style={labelStyle}>{t('income_range')}</label><input style={inputStyle} value={form.income_range} onChange={update('income_range')} placeholder="e.g. 100,000 - 150,000 PKR/month" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('religion_label')}</label><input style={inputStyle} value={form.religion} onChange={update('religion')} /></div>
          <div><label style={labelStyle}>{t('mtr_sect')}</label><input style={inputStyle} value={form.sect} onChange={update('sect')} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('mother_tongue')}</label><input style={inputStyle} value={form.mother_tongue} onChange={update('mother_tongue')} /></div>
          <div><label style={labelStyle}>{t('mtr_diet')}</label>
            <select style={inputStyle} value={form.diet} onChange={update('diet')}>
              <option value="">— {t('mtr_not_spec')} —</option>
              {DIET_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={labelStyle}>{t('fathers_occupation')}</label><input style={inputStyle} value={form.father_occupation} onChange={update('father_occupation')} /></div>
          <div><label style={labelStyle}>{t('num_siblings')}</label><input type="number" style={inputStyle} value={form.siblings_count || ''} onChange={update('siblings_count')} /></div>
        </div>
        <div><label style={labelStyle}>{t('mtr_family')}</label><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={form.family_details} onChange={update('family_details')} /></div>
        <div><label style={labelStyle}>{t('about_you')}</label><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={3} value={form.bio} onChange={update('bio')} /></div>
        <div><label style={labelStyle}>{t('looking_for')}</label><textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={form.looking_for} onChange={update('looking_for')} /></div>
        <div><label style={labelStyle}>{t('main_photo')}</label><input type="file" accept="image/*" onChange={(e) => setPhotoFile(e.target.files[0])} /></div>
      </div>
      {hasBlueTick && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <input type="checkbox" id="matri-autopost" checked={autoPost} onChange={(e) => setAutoPost(e.target.checked)} />
          <label htmlFor="matri-autopost" style={{ fontSize: 13, margin: 0 }}>{t('auto_post_perk')}</label>
        </div>
      )}
      <button onClick={submit} disabled={submitting} style={{ width: '100%', padding: '10px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer', marginTop: 12 }}>
        {submitting ? t('mtr_saving') : t('mtr_save')}
      </button>
      {existing && <div style={{ marginTop: 16, borderTop: `0.5px solid ${colors.border}`, paddingTop: 12 }}><GalleryManager profile={existing} onChanged={onSaved} /></div>}
    </div>
  );
}

function ProfileCard({ profile, onOpen }) {
  return (
    <div onClick={() => onOpen(profile.id)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}>
      <div style={{ width: '100%', aspectRatio: '1', background: '#F1EFE8', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {profile.photo ? (
          <img src={profile.photo} alt={profile.username} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: profile.photo_locked ? 'blur(14px)' : 'none' }} />
        ) : <span style={{ fontSize: 32, opacity: 0.3 }}>👤</span>}
        {profile.photo_locked && <span style={{ position: 'absolute', fontSize: 22 }}>🔒</span>}
        {profile.is_boosted && <span style={{ position: 'absolute', top: 6, left: 6, fontSize: 10, background: colors.gold, color: '#4A3300', padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>📢 {t('mtr_featured')}</span>}
        {profile.is_verified && <span style={{ position: 'absolute', top: 6, right: 6, fontSize: 10, background: colors.primaryBg, color: colors.primary, padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>✅ {t('mtr_verified')}</span>}
      </div>
      <div style={{ padding: '0.65rem' }}>
        <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 2px' }}>@{profile.username}, {profile.age}</p>
        <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 2px' }}>{profile.city}{profile.country ? `, ${profile.country}` : ''}</p>
        <p style={{ fontSize: 11, color: colors.textMuted, margin: 0 }}>{profile.profession}</p>
      </div>
    </div>
  );
}

function ProfileDetailModal({ profileId, onClose }) {
  const [profile, setProfile] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [loadErr, setLoadErr] = useState('');

  const load = useCallback(async () => {
    setLoadErr('');
    try {
      const res = await fetch(`${API_BASE}/profile/${profileId}/`, { headers: authHeaders() });
      const data = await safeJson(res);
      setProfile(data);
    } catch (e) {
      // Pehle ye khamoshi se fail hota tha aur modal hamesha
      // "Loading..." par atka rehta tha.
      setLoadErr(networkMessage(e));
    }
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const toggleShortlist = async () => {
    try {
      const res = await fetch(`${API_BASE}/profile/${profileId}/shortlist/`, { method: 'POST', headers: authHeaders() });
      const data = await safeJson(res);
      setProfile((p) => ({ ...p, is_shortlisted: data.shortlisted }));
    } catch (e) {}
  };

  const sendRequest = async () => {
    setSending(true);
    setMsg('');
    try {
      const res = await fetch(`${API_BASE}/profile/${profileId}/send-request/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ message }) });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.error || 'Could not send request.');
      setMsg(t('mtr_req_sent'));
    } catch (e) { setMsg(e.message); } finally { setSending(false); }
  };

  const detailRow = (label, value) => value ? (
    <p style={{ fontSize: 13, margin: '4px 0' }}><span style={{ color: colors.textMuted }}>{label}:</span> {value}</p>
  ) : null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px', overflowY: 'auto' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, maxWidth: 560, width: '100%', padding: '1.5rem', marginTop: 20 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: colors.textMuted }}>✕</button>
        </div>
        {!profile ? <p style={{ textAlign: 'center', color: colors.textMuted }}>{t('loading')}</p> : (
          <>
            <div style={{ position: 'relative', marginBottom: 12 }}>
              {profile.photo ? (
                <img src={profile.photo} alt={profile.username} style={{ width: '100%', borderRadius: 10, filter: profile.photo_locked ? 'blur(18px)' : 'none' }} />
              ) : <div style={{ width: '100%', aspectRatio: '1', background: '#F1EFE8', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>👤</div>}
              {profile.photo_locked && <p style={{ position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 12, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>🔒 {t('mtr_photo_lock')}</p>}
            </div>

            {profile.gallery?.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto' }}>
                {profile.gallery.map((url, i) => <img key={i} src={url} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, filter: profile.photo_locked ? 'blur(10px)' : 'none' }} />)}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <h2 style={{ fontSize: 18, margin: 0 }}>@{profile.username}, {profile.age}</h2>
              {profile.is_verified && <span style={{ fontSize: 10, background: colors.primaryBg, color: colors.primary, padding: '2px 8px', borderRadius: 8, fontWeight: 700 }}>✅ {t('mtr_verified')}</span>}
            </div>
            <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 12px' }}>{profile.city}{profile.country ? `, ${profile.country}` : ''}</p>

            {detailRow('Height', profile.height_display)}
            {detailRow('Marital status', MARITAL_STATUS.find((m) => m[0] === profile.marital_status)?.[1])}
            {detailRow('Education', profile.education)}
            {detailRow('Profession', profile.profession)}
            {detailRow('Income', profile.income_range)}
            {detailRow('Religion', profile.religion)}
            {detailRow('Sect', profile.sect)}
            {detailRow('Mother tongue', profile.mother_tongue)}
            {detailRow('Diet', DIET_OPTIONS.find((d) => d[0] === profile.diet)?.[1])}
            {detailRow("Father's occupation", profile.father_occupation)}
            {detailRow('Siblings', profile.siblings_count)}
            {detailRow('Family background', profile.family_details)}

            {profile.bio && <p style={{ fontSize: 14, lineHeight: 1.5, margin: '12px 0' }}>{profile.bio}</p>}
            {profile.looking_for && <p style={{ fontSize: 13, color: colors.textMuted, fontStyle: 'italic', marginBottom: 16 }}>Looking for: {profile.looking_for}</p>}

            {!profile.is_own_profile && (
              <>
                <button onClick={toggleShortlist} style={{ width: '100%', padding: '8px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 8, background: profile.is_shortlisted ? colors.primaryBg : '#fff', color: profile.is_shortlisted ? colors.primary : colors.text, cursor: 'pointer', marginBottom: 10 }}>
                  {profile.is_shortlisted ? '⭐ Shortlisted' : '☆ Add to Shortlist'}
                </button>
                <textarea placeholder={t('add_message_optional')} value={message} onChange={(e) => setMessage(e.target.value)} rows={2} style={{ width: '100%', padding: '8px', fontSize: 13, border: `0.5px solid ${colors.border}`, borderRadius: 6, boxSizing: 'border-box', marginBottom: 10, resize: 'vertical' }} />
                <button onClick={sendRequest} disabled={sending} style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 8, background: colors.primary, color: '#fff', cursor: 'pointer' }}>
                  {sending ? 'Sending...' : '💌 Send Match Request'}
                </button>
                {msg && <p style={{ fontSize: 12, color: msg.includes('sent') ? colors.primary : colors.danger, marginTop: 8 }}>{msg}</p>}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function RequestsPanel({ onOpen }) {
  const [tab, setTab] = useState('received');
  const [data, setData] = useState({ sent: [], received: [], matched: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/requests/mine/`, { headers: authHeaders() });
      setData(await safeJson(res));
    } catch (e) {
      console.error('Could not load match requests:', e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (id, decision) => {
    try {
      await fetch(`${API_BASE}/requests/${id}/respond/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ decision }) });
      load();
    } catch (e) {}
  };

  const list = data[tab] || [];

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <button onClick={() => setTab('received')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: tab === 'received' ? colors.primaryBg : '#fff', color: tab === 'received' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('received_tab')}</button>
        <button onClick={() => setTab('sent')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: tab === 'sent' ? colors.primaryBg : '#fff', color: tab === 'sent' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>{t('sent_tab')}</button>
        <button onClick={() => setTab('matched')} style={{ padding: '4px 12px', fontSize: 12, borderRadius: 14, border: `0.5px solid ${colors.border}`, background: tab === 'matched' ? colors.primaryBg : '#fff', color: tab === 'matched' ? colors.primary : colors.textMuted, cursor: 'pointer' }}>💚 {t('mtr_matched')}</button>
      </div>

      {loading ? <p style={{ color: colors.textMuted, textAlign: 'center' }}>{t('loading')}</p> :
        list.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('nothing_here_yet')}</p> :
        list.map((r) => (
          <div key={r.id} onClick={() => onOpen(r.other_profile_id)} style={{ background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.75rem', marginBottom: 8, cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>@{r.other_username}</span>
              <span style={{ fontSize: 12, color: colors.textMuted, textTransform: 'capitalize' }}>{r.status}</span>
            </div>
            {r.message && <p style={{ fontSize: 12, color: colors.textMuted, margin: '2px 0' }}>"{r.message}"</p>}
            {tab === 'received' && r.status === 'pending' && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }} onClick={(e) => e.stopPropagation()}>
                <button onClick={() => respond(r.id, 'accepted')} style={{ fontSize: 12, padding: '4px 12px', border: 'none', borderRadius: 10, background: colors.primary, color: '#fff', cursor: 'pointer' }}>{t('accept_btn')}</button>
                <button onClick={() => respond(r.id, 'rejected')} style={{ fontSize: 12, padding: '4px 12px', border: `0.5px solid ${colors.border}`, borderRadius: 10, background: '#fff', cursor: 'pointer' }}>{t('decline')}</button>
              </div>
            )}
            {r.status === 'accepted' && (
              <a href={`/chat/${r.other_username}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: 12, padding: '6px 12px', border: 'none', borderRadius: 6, background: colors.primary, color: '#fff', cursor: 'pointer', textDecoration: 'none', display: 'inline-block', marginTop: 6 }}>💬 {t('mtr_chat')}</a>
            )}
          </div>
        ))
      }
    </div>
  );
}

function ShortlistPanel({ onOpen }) {
  const [profiles, setProfiles] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/shortlist/mine/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => setProfiles(d.profiles || [])).catch(() => setProfiles([]));
  }, []);

  if (profiles === null) return <p style={{ color: colors.textMuted, textAlign: 'center' }}>{t('loading')}</p>;
  if (profiles.length === 0) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_shortlisted')}</p>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
      {profiles.map((p) => <ProfileCard key={p.id} profile={p} onOpen={onOpen} />)}
    </div>
  );
}

function WhoViewedMePanel({ onOpen }) {
  const [viewers, setViewers] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/who-viewed-me/`, { headers: authHeaders() }).then((r) => r.json()).then((d) => setViewers(d.viewers || [])).catch(() => setViewers([]));
  }, []);

  if (viewers === null) return <p style={{ color: colors.textMuted, textAlign: 'center' }}>{t('loading')}</p>;
  if (viewers.length === 0) return <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_one_viewed')}</p>;

  return (
    <div>
      {viewers.map((v, i) => (
        <div key={i} onClick={() => v.profile_id && onOpen(v.profile_id)} style={{ display: 'flex', justifyContent: 'space-between', background: colors.surface, border: `0.5px solid ${colors.border}`, borderRadius: 10, padding: '0.6rem 0.75rem', marginBottom: 6, cursor: v.profile_id ? 'pointer' : 'default' }}>
          <span style={{ fontSize: 13 }}>@{v.username}</span>
          <span style={{ fontSize: 11, color: colors.textMuted }}>{new Date(v.viewed_at).toLocaleDateString()}</span>
        </div>
      ))}
    </div>
  );
}

function MyProfileTab({ profile, onSaved }) {
  const [boosting, setBoosting] = useState(false);

  const goBoost = async () => {
    if (!window.Pi) { alert('Open inside Pi Browser to pay with Pi.'); return; }
    setBoosting(true);
    try {
      await window.Pi.authenticate(['username', 'payments'], () => {});
      window.Pi.createPayment(
        { amount: 1, memo: 'Matrimony profile boost', metadata: {} },
        {
          onReadyForServerApproval: (paymentId) => fetch(`${API_BASE}/boost/approve/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId }) }),
          onReadyForServerCompletion: (paymentId, txid) => {
            fetch(`${API_BASE}/boost/complete/`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ payment_id: paymentId, txid, amount: 1, days: 7 }) })
              .then(() => { alert('✅ Thanks for boosting! — Aap ka profile agle 7 din tak top par dikhega, zyada logon tak pohnchega.'); setBoosting(false); onSaved(); })
              .catch(() => setBoosting(false));
          },
          onCancel: () => setBoosting(false),
          onError: () => setBoosting(false),
        }
      );
    } catch (e) { setBoosting(false); }
  };

  return (
    <div>
      {profile?.exists && !profile.is_boosted && (
        <button
          onClick={() => {
            fetch('/api/cart/add/', {
              method: 'POST', headers: authHeaders(),
              body: JSON.stringify({
                app_name: 'matrimony', item_type: 'boost', reference_id: profile.id || 0,
                title: 'Matrimony Profile Boost', thumbnail_url: '', price_pi: 1, metadata: { days: 7 },
              }),
            }).then(() => alert('Added to cart! View it from the 🛒 My Cart menu.'));
          }}
          style={{ width: '100%', marginBottom: 12, padding: '10px', fontSize: 13, fontWeight: 500, border: 'none', borderRadius: 8, background: '#FFF3C4', color: '#8A6D00', cursor: 'pointer' }}
        >
          🛒 Add to Cart - Boost profile (1 Pi / 7 days)
        </button>
      )}
      {profile?.is_boosted && <p style={{ fontSize: 13, color: colors.primary, marginBottom: 12 }}>📢 {t('mtr_boosted')}</p>}
      <ProfileSetupForm existing={profile?.exists ? profile : null} onSaved={onSaved} />
    </div>
  );
}

export default function MatrimonyApp() {
  const [tab, setTab] = useState('browse');
  const [profile, setProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProfileId, setOpenProfileId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({ gender: '', city: '', marital_status: '', religion: '', min_age: '', max_age: '' });

  const loadProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/profile/mine/`, { headers: authHeaders() });
      if (res.status === 404) { setProfile(null); return; }   // profile bana hi nahi
      setProfile(await safeJson(res));
    } catch (e) {
      // Pehle nakaami par profile null reh jata tha, is liye
      // "Set up your profile first" ki jhooti warning aati thi.
      console.error('Could not load your matrimony profile:', e);
    }
  }, []);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await fetch(`${API_BASE}/browse/?${params.toString()}`, { headers: authHeaders() });
      const data = await safeJson(res);
      setProfiles(data.profiles || []);
    } catch (e) {
      console.error('Could not load profiles:', e);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadProfile(); }, [loadProfile]);
  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  const tabStyle = (t) => ({ padding: '8px 14px', fontSize: 13, fontWeight: 500, border: 'none', borderBottom: tab === t ? `2px solid ${colors.primary}` : '2px solid transparent', background: 'transparent', color: tab === t ? colors.primary : colors.textMuted, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' });
  const inputStyle = { padding: '8px 10px', fontSize: 13, border: '0.5px solid #B4B2A9', borderRadius: 6 };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif', background: colors.bg, minHeight: '100vh' }}>
      {showHelp && <MatrimonyHelpModal onClose={() => setShowHelp(false)} />}
      {openProfileId && <ProfileDetailModal profileId={openProfileId} onClose={() => setOpenProfileId(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>💗 {t('mtr_app')}</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 1rem' }}>{t('matrimony_tagline')}</p>
        </div>
        <button onClick={() => setShowHelp(true)} style={{ padding: '6px 14px', fontSize: 13, fontWeight: 500, border: `0.5px solid ${colors.border}`, borderRadius: 20, background: '#fff', cursor: 'pointer' }}>❓ How it works</button>
      </div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: `0.5px solid ${colors.border}`, marginBottom: '1.25rem' }}>
        <button style={tabStyle('browse')} onClick={() => setTab('browse')}>{t('browse')}</button>
        <button style={tabStyle('profile')} onClick={() => setTab('profile')}>{t('my_profile')}</button>
        <button style={tabStyle('requests')} onClick={() => setTab('requests')}>{t('requests_tab')}</button>
        <button style={tabStyle('shortlist')} onClick={() => setTab('shortlist')}>⭐ {t('mtr_shortlist')}</button>
        <button style={tabStyle('viewers')} onClick={() => setTab('viewers')}>👁️ {t('mtr_viewed')}</button>
      </div>

      {tab === 'browse' && (
        <>
          {!profile?.exists && (
            <div style={{ background: '#FBEAE3', color: colors.danger, padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              Set up your profile first (My Profile tab) before browsing.
            </div>
          )}
          <button onClick={() => setShowFilters(!showFilters)} style={{ marginBottom: 10, padding: '6px 14px', fontSize: 12, border: `0.5px solid ${colors.border}`, borderRadius: 16, background: '#fff', cursor: 'pointer' }}>
            {showFilters ? 'Hide filters' : '🔍 Filters'}
          </button>
          {showFilters && (
            <div style={{ background: '#F1EFE8', borderRadius: 10, padding: '0.75rem', marginBottom: 12, display: 'grid', gap: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <select style={inputStyle} value={filters.gender} onChange={(e) => setFilters((f) => ({ ...f, gender: e.target.value }))}>
                  <option value="">{t('any_gender')}</option><option value="male">{t('male')}</option><option value="female">{t('female')}</option>
                </select>
                <input style={inputStyle} placeholder={t('city')} value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <input style={inputStyle} type="number" placeholder={t('min_age')} value={filters.min_age} onChange={(e) => setFilters((f) => ({ ...f, min_age: e.target.value }))} />
                <input style={inputStyle} type="number" placeholder={t('max_age')} value={filters.max_age} onChange={(e) => setFilters((f) => ({ ...f, max_age: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <select style={inputStyle} value={filters.marital_status} onChange={(e) => setFilters((f) => ({ ...f, marital_status: e.target.value }))}>
                  <option value="">{t('any_marital_status')}</option>
                  {MARITAL_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input style={inputStyle} placeholder={t('religion_label')} value={filters.religion} onChange={(e) => setFilters((f) => ({ ...f, religion: e.target.value }))} />
              </div>
            </div>
          )}

          {loading ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('loading_profiles')}</p> :
            profiles.length === 0 ? <p style={{ color: colors.textMuted, textAlign: 'center', padding: '2rem 0' }}>{t('no_profiles_match')}</p> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                {profiles.map((p) => <ProfileCard key={p.id} profile={p} onOpen={setOpenProfileId} />)}
              </div>
            )
          }
        </>
      )}

      {tab === 'profile' && <MyProfileTab profile={profile} onSaved={loadProfile} />}
      {tab === 'requests' && <RequestsPanel onOpen={setOpenProfileId} />}
      {tab === 'shortlist' && <ShortlistPanel onOpen={setOpenProfileId} />}
      {tab === 'viewers' && <WhoViewedMePanel onOpen={setOpenProfileId} />}
    </div>
  );
}
